import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import DashboardLayout from '../layouts/DashboardLayout';
import DraftReviewCard from '../components/drafts/DraftReviewCard';
import { Loader2, Inbox, ClipboardCheck } from 'lucide-react';
import { UrgencyBadge } from '../components/ui/urgency-badge';
const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Drafts to Review queue (`/drafts`).
 *
 * Pulls all wave_assignments where status=content_uploaded for the current
 * brand, sorts oldest-first (most-overdue surfaces top), and renders one
 * `DraftReviewCard` per draft. The first GET also fires a one-time "Under
 * Consideration" FCM push to each creator (server-side idempotent).
 */
export default function DraftsQueuePage() {
  const [drafts, setDrafts] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/brand/drafts/queue?limit=50`, { withCredentials: true });
      setDrafts(data.drafts || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load drafts');
      setDrafts([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleResolved = useCallback(({ id }) => {
    // Optimistic: drop the resolved card after a short pause so the user sees the green banner.
    setTimeout(() => setDrafts(d => (d || []).filter(x => x.id !== id)), 1200);
  }, []);

  return (
    <DashboardLayout>
      <div className="p-4 lg:p-6 max-w-[1280px] mx-auto" data-testid="drafts-queue-page">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-2xl font-black text-white flex items-center gap-2">
              <ClipboardCheck className="h-6 w-6 text-[#00D4FF]" /> Review Queue
            </h1>
            <p className="text-xs text-gray-500 mt-1">
              Approve, request changes, or pass on each draft. Every decision sends a kind, brand-voiced note to the creator. 48-hour SLA.
            </p>
          </div>
          {drafts && drafts.length > 0 && (
            <UrgencyBadge
              level="warning"
              label={`${drafts.length} awaiting`}
              testId="drafts-queue-count"
              icon={false}
            />
          )}
        </div>

        {drafts === null && (
          <div className="flex items-center gap-3 text-sm text-gray-500" data-testid="drafts-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading queue…
          </div>
        )}

        {error && drafts !== null && (
          <div className="text-[11px] text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4" data-testid="drafts-error">{error}</div>
        )}

        {drafts && drafts.length === 0 && (
          <div className="rounded-2xl border border-dashed border-[#1f1f1f] bg-[#0A0A0A] p-12 text-center" data-testid="drafts-empty">
            <Inbox className="h-10 w-10 mx-auto mb-3 text-gray-600" />
            <h3 className="text-base font-bold text-gray-300 mb-1">All caught up.</h3>
            <p className="text-xs text-gray-500 max-w-md mx-auto">
              No drafts pending review. When a creator uploads their content for an active wave, it will land here for your decision.
            </p>
          </div>
        )}

        {drafts && drafts.length > 0 && (
          <div className="space-y-4">
            {drafts.map(d => (
              <DraftReviewCard key={d.id} draft={d} onResolved={handleResolved} />
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
